export type EmploymentStatus =
  | "ACTIVE"
  | "ON_PROBATION"
  | "ON_LEAVE"
  | "SUSPENDED"
  | "TERMINATED"
  | "RESIGNED";

export type EmploymentType =
  | "PERMANENT"
  | "CONTRACT"
  | "INTERN"
  | "CASUAL"
  | "CONSULTANT"
  | "TEMPORARY"
  | "OTHER";

export type Gender = "MALE" | "FEMALE" | "OTHER";
export type MaritalStatus = "SINGLE" | "MARRIED" | "DIVORCED" | "WIDOWED";

export type DepartmentMini = {
  id: number;
  code: string;
  name: string;
};

export type DepartmentOption = DepartmentMini & {
  description?: string | null;
  active?: boolean;
};

export type EmployeeUserSummary = {
  id: number;
  username: string;
  email: string;
  enabled: boolean;
  locked: boolean;
  roles: string[];
  permissions?: string[];
};

export type EmployeeRow = {
  id: number;
  employeeNumber: string;
  fullName: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  workEmail?: string | null;
  personalEmail?: string | null;
  mobilePhone?: string | null;
  alternativePhone?: string | null;
  jobTitle?: string | null;
  /**
   * Primary field name used by the backend in both filter params and list responses.
   * The frontend type previously called this `employmentStatus` but the backend
   * consistently uses `status`.  Both names are accepted here for compatibility.
   */
  status?: EmploymentStatus | null;
  employmentStatus?: EmploymentStatus | null;
  employmentType?: EmploymentType | null;
  primaryDepartmentId?: number | null;
  primaryDepartmentCode?: string | null;
  departmentName?: string | null;
  departments?: DepartmentMini[];
  hireDate?: string | null;
  /**
   * Planned end date of a fixed-term engagement (intern / contract / temporary).
   * Distinct from terminationDate -- this does NOT affect current active status.
   * EmployeeContractExpiryScheduler auto-deactivates the employee once it passes.
   */
  contractEndDate?: string | null;
  systemAccessEnabled?: boolean;
  businessCardEnabled?: boolean;
  publicProfileToken?: string | null;
  roleCodes?: string[];
  user?: EmployeeUserSummary | null;
};

/** Resolve whichever employment-status field name the backend returns. */
export function resolveStatus(emp: EmployeeRow): EmploymentStatus | null {
  return emp.employmentStatus ?? emp.status ?? null;
}

export type EmployeeDetails = EmployeeRow & {
  dateOfBirth?: string | null;
  gender?: Gender | string | null;
  maritalStatus?: MaritalStatus | string | null;
  nationalIdNumber?: string | null;
  kraPin?: string | null;
  nhifNumber?: string | null;
  nssfNumber?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  cityTown?: string | null;
  country?: string | null;
  probationEndDate?: string | null;
  terminationDate?: string | null;
  emergencyContactName?: string | null;
  emergencyContactRelationship?: string | null;
  emergencyContactPhone?: string | null;
};

export type EmployeeFormValues = {
  employeeNumber: string;
  userId: number | "";
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  maritalStatus: string;
  nationalIdNumber: string;
  kraPin: string;
  nhifNumber: string;
  nssfNumber: string;
  personalEmail: string;
  workEmail: string;
  mobilePhone: string;
  alternativePhone: string;
  addressLine1: string;
  addressLine2: string;
  cityTown: string;
  country: string;
  hireDate: string;
  probationEndDate: string;
  terminationDate: string;
  contractEndDate: string;
  employmentStatus: string;
  employmentType: string;
  jobTitle: string;
  primaryDepartmentId: number | "";
  departmentIds: number[];
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: string;
  systemAccessEnabled: boolean;
  businessCardEnabled: boolean;
  roleCodes: string[];
};

export type EmployeeFilters = {
  page?: number;
  size?: number;
  search?: string;
  status?: EmploymentStatus | "ALL";
  employmentType?: EmploymentType | "ALL";
  departmentId?: number | "";
};

export type PageResponse<T> = {
  content: T[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
};

export type RelationshipTypeOption = {
  id: number;
  code: string;
  name: string;
};

export type GenderOption = {
  id: number;
  code: string;
  name: string;
};

export type FamilyMember = {
  id: number;
  relationshipTypeId: number;
  relationshipTypeCode: string;
  relationshipTypeName: string;
  fullName: string;
  dateOfBirth?: string | null;
  genderId?: number | null;
  genderCode?: string | null;
  genderName?: string | null;
  dependent: boolean;
  nextOfKin: boolean;
  notes?: string | null;
};

export type FamilyMemberFormValues = {
  relationshipTypeId: number | "";
  fullName: string;
  dateOfBirth: string;
  genderId: number | "";
  dependent: boolean;
  nextOfKin: boolean;
  notes: string;
};

export type EmployeeImportRowResult = {
  rowNumber: number;
  employeeNumber?: string | null;
  success: boolean;
  message: string;
  employeeId?: number | null;
};

export type EmployeeImportSummary = {
  totalRows: number;
  successCount: number;
  errorCount: number;
  rows: EmployeeImportRowResult[];
};

export type RoleOption = {

  id: number;

  code: string;

  name: string;

  description?: string | null;

  active: boolean;

};